import React from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Button,
  Stack,
} from '@mui/material';
import { PictureAsPdf, OpenInNew } from '@mui/icons-material';
import { publicationsData } from '../data/publicationsData';

const Publications = () => {
  // Latest first
  const sortedPublications = [...publicationsData].sort((a, b) =>
    new Date(b.publishDate) - new Date(a.publishDate)
  );

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc', pt: 8, pb: 12 }}>
      <Container maxWidth="lg">
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 800, color: 'rgb(20, 50, 90)', mb: 2 }}>
            Publications
          </Typography>
          <Typography variant="h6" sx={{ color: 'text.secondary', maxWidth: '700px', mx: 'auto', fontWeight: 400 }}>
            Peer-reviewed papers and preprints from the Genion Quantum Technologies team and our research collaborators.
          </Typography>
        </Box>

        <Stack spacing={4}>
          {sortedPublications.map((pub) => (
            <Card key={pub.id} component="article" sx={{
              borderRadius: 4,
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
              border: '1px solid #e2e8f0',
            }}>
              <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
                  <Chip
                    label={pub.venue}
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(49, 110, 177, 0.1)',
                      color: 'rgb(49, 110, 177)',
                      fontWeight: 700,
                      fontSize: '0.7rem'
                    }}
                  />
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    {pub.date}
                  </Typography>
                </Box>

                <Typography variant="h4" component="h2" sx={{
                  fontWeight: 700,
                  color: 'rgb(20, 50, 90)',
                  mb: 2,
                  fontSize: { xs: '1.35rem', md: '1.75rem' },
                  lineHeight: 1.3
                }}>
                  {pub.title}
                </Typography>

                <Typography variant="body2" sx={{ color: '#64748b', mb: 3, lineHeight: 1.6 }}>
                  {pub.authors}
                </Typography>

                {pub.abstract.map((paragraph, idx) => (
                  <Typography key={idx} variant="body1" sx={{ color: '#475569', mb: 2, lineHeight: 1.7 }}>
                    {paragraph}
                  </Typography>
                ))}

                {pub.keywords && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 3, mb: 4 }}>
                    {pub.keywords.map((keyword) => (
                      <Chip key={keyword} label={keyword} size="small" variant="outlined" sx={{ color: '#475569' }} />
                    ))}
                  </Box>
                )}

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Button
                    variant="contained"
                    href={encodeURI(pub.pdf)}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<PictureAsPdf />}
                    sx={{ fontWeight: 700, backgroundColor: 'rgb(49, 110, 177)' }}
                  >
                    Read PDF
                  </Button>
                  {pub.doi && (
                    <Button
                      variant="outlined"
                      href={`https://doi.org/${pub.doi}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      endIcon={<OpenInNew />}
                      sx={{ fontWeight: 700 }}
                    >
                      DOI: {pub.doi}
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      </Container>
    </Box>
  );
};

export default Publications;
